import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Divider, Alert,
  CircularProgress, Grid, MenuItem, Accordion, AccordionSummary, AccordionDetails, Chip,
  FormControlLabel, Switch
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../../services/api';

/**
 * Context & memory architecture settings.
 *
 * Every threshold here is configurable rather than constant on purpose: the values were
 * first derived from test data with almost no long-running real therapy in it, and
 * message rates vary ~50x between clients (1.6/day to 84.6/day in production), so
 * anything fixed in code would be fitted to an artefact.
 *
 * Design: docs/context-memory/context-and-memory-architecture.md
 */

const DEFAULTS = {
  episodeGapHours: 6,
  episodeMaxMessages: 60,
  episodeMaxSpanHours: 168,
  summaryMaxTokens: 400,
  summaryRebuildEveryNRevisions: 10,
  summaryInflationAlertRatio: 1.5,
  summarisationPrompt: '',
  contextTokenBudget: 60000,
  recentVerbatimMinMessages: 20,
  redactionDetectorVersion: 'v1',
  redactionAggressiveness: 'balanced'
};

// What the AI Supporter sees of video sessions with the human therapist (29 Sep 2026).
// Platform default; service groups, therapists and single calls can override it.
const CALL_DEFAULTS = {
  enabled: true,
  includeTranscript: true,
  maxCalls: 3,
  lookbackDays: 90,
  transcriptMaxChars: 12000
};

const ContextMemorySettings = () => {
  const [callSettings, setCallSettings] = useState(CALL_DEFAULTS);
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [inflation, setInflation] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const platform = await api.getPlatformSettings();
        setSettings({ ...DEFAULTS, ...(platform?.contextMemorySettings || {}) });
        setCallSettings({ ...CALL_DEFAULTS, ...(platform?.callContextForAISettings || {}) });
      } catch (err) {
        setError(err.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // The monitored invariant. The ClinicalFormulation used the same incremental pattern
  // with nothing measuring it and inflated for 60+ revisions unnoticed.
  const loadInflation = async () => {
    try {
      const res = await api.get('/context-memory/inflation-report');
      setInflation(res?.report || res?.data?.report || []);
    } catch (err) {
      setError(`Could not load the inflation report: ${err.message}`);
    }
  };

  const set = (key) => (e) => {
    const raw = e.target.value;
    const numeric = typeof DEFAULTS[key] === 'number';
    setSettings(s => ({ ...s, [key]: numeric ? (raw === '' ? '' : Number(raw)) : raw }));
  };

  const setCall = (key) => (e) => {
    const value = typeof CALL_DEFAULTS[key] === 'boolean'
      ? e.target.checked
      : (e.target.value === '' ? '' : Number(e.target.value));
    setCallSettings(s => ({ ...s, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(false);
    try {
      await api.updatePlatformSettings({ contextMemorySettings: settings, callContextForAISettings: callSettings });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Context &amp; Memory</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How much of a client&apos;s history the AI can see, how older material is compressed,
        and how identifiable detail is handled. These features are gated per service group —
        changing settings here has no effect until a group has the relevant capability enabled.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Settings saved.</Alert>}

      {/* --- episodes --- */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Episode segmentation</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            History is cut into episodes by conversation rhythm rather than message count.
            The same number of messages means very different things for different clients,
            so a fixed count produces summaries that start and stop mid-topic.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Gap that starts a new episode (hours)"
                value={settings.episodeGapHours} onChange={set('episodeGapHours')}
                helperText="A pause longer than this ends the current episode" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Maximum messages per episode"
                value={settings.episodeMaxMessages} onChange={set('episodeMaxMessages')}
                helperText="Stops a busy day with no pause becoming one huge episode" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Maximum episode span (hours)"
                value={settings.episodeMaxSpanHours} onChange={set('episodeMaxSpanHours')}
                helperText="Stops a sparse conversation becoming one episode spanning months" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* --- summarisation --- */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Summarisation</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Older episodes are compressed so they still reach the AI once the verbatim
            messages no longer fit. These summaries are read by the model, not by the
            client — they are a different artefact from the client and therapist summaries
            shown elsewhere.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Maximum summary size (tokens)"
                value={settings.summaryMaxTokens} onChange={set('summaryMaxTokens')}
                helperText="Enforced in code, not requested in the prompt" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Rebuild after N revisions"
                value={settings.summaryRebuildEveryNRevisions} onChange={set('summaryRebuildEveryNRevisions')}
                helperText="Full rebuild from the original messages, bounding drift" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" inputProps={{ step: 0.1 }} label="Inflation alert ratio"
                value={settings.summaryInflationAlertRatio} onChange={set('summaryInflationAlertRatio')}
                helperText="Alert when a summary grows by more than this multiple" />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>Summarisation prompt</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Leave empty to use the built-in prompt. If you replace it, keep the rule that
            summaries record <strong>what the client said</strong> rather than asserting it as
            fact — a summary that turns &ldquo;the client said X&rdquo; into &ldquo;X&rdquo;
            converts a report into a fact permanently, and the AI will then treat it as
            established.
          </Typography>
          <TextField fullWidth multiline minRows={6} label="Custom summarisation prompt"
            value={settings.summarisationPrompt} onChange={set('summarisationPrompt')}
            placeholder="Empty = use the built-in prompt" />
          <Typography variant="caption" color="text.secondary">
            Each summary records which prompt produced it, so changing this does not make
            existing summaries unexplainable.
          </Typography>
        </CardContent>
      </Card>

      {/* --- video sessions --- */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Video sessions and the AI Supporter</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Gives the AI Supporter the client&apos;s recent video sessions with their therapist:
            the therapist&apos;s clinical note, the client-facing summary, themes and any risk
            recorded. The AI uses the clinical note for understanding only and shares only
            client-facing content with the client. This is the platform default: service
            groups (Features), therapists and single calls can override it. Risk recorded on
            a call always reaches the AI, whatever is set here.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch id="call-context-enabled" checked={!!callSettings.enabled} onChange={setCall('enabled')} />}
                label="AI Supporter sees video sessions"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch id="call-context-transcript" checked={!!callSettings.includeTranscript} disabled={!callSettings.enabled} onChange={setCall('includeTranscript')} />}
                label="Include the transcript of the most recent session"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Sessions included"
                value={callSettings.maxCalls} onChange={setCall('maxCalls')}
                inputProps={{ min: 1, max: 10 }} helperText="Most recent first" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Look back (days)"
                value={callSettings.lookbackDays} onChange={setCall('lookbackDays')}
                inputProps={{ min: 7, max: 365 }} helperText="Older sessions are left out" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Transcript length (characters)"
                value={callSettings.transcriptMaxChars} onChange={setCall('transcriptMaxChars')}
                inputProps={{ min: 1000, max: 60000 }} helperText="Longer transcripts keep the opening and the close" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* --- context budget --- */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Context budget</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Caps how much is sent to the model per turn. Safety disclosures and client
            boundaries are exempt from this budget at any age — they are never dropped to
            make room.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Token budget per turn"
                value={settings.contextTokenBudget} onChange={set('contextTokenBudget')}
                helperText="Replaces the old fixed 500-message window" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Minimum verbatim messages"
                value={settings.recentVerbatimMinMessages} onChange={set('recentVerbatimMinMessages')}
                helperText="Recent messages always kept in full, whatever the budget says" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* --- redaction --- */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Redaction</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Produces a redacted copy for secondary use. The original transcripts and
            summaries are never altered, so redaction can be re-run and improved later.
            This is separate from erasing a client&apos;s data, which is irreversible and
            done per client.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Aggressiveness"
                value={settings.redactionAggressiveness} onChange={set('redactionAggressiveness')}
                helperText="Higher settings remove more, at the cost of clinical detail">
                <MenuItem value="conservative">Conservative — contact details only</MenuItem>
                <MenuItem value="balanced">Balanced — contact details and locations</MenuItem>
                <MenuItem value="aggressive">Aggressive — everything detectable</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Detector version"
                value={settings.redactionDetectorVersion} onChange={set('redactionDetectorVersion')}
                helperText="Change this to regenerate every redacted copy" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* --- monitoring --- */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Summary size monitoring</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Summaries should stay roughly the same size as they are revised. Steady growth
            means they are accumulating rather than compressing, which is how a summary
            quietly stops being useful.
          </Typography>
          <Button variant="outlined" onClick={loadInflation} sx={{ mb: 2 }}>
            Load report
          </Button>
          {inflation && inflation.length === 0 && (
            <Alert severity="info">No summaries yet.</Alert>
          )}
          {inflation && inflation.length > 0 && (
            <Box>
              {inflation.map(row => (
                <Box key={row._id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
                  <Chip size="small" label={`revision ${row._id}`} />
                  <Typography variant="body2">{row.episodes} episodes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    average {Math.round(row.avgTokens)} tokens, largest {row.maxTokens}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
        <Button variant="outlined" onClick={() => { setSettings(DEFAULTS); setCallSettings(CALL_DEFAULTS); }} disabled={saving}>
          Reset to defaults
        </Button>
      </Box>
    </Box>
  );
};

export default ContextMemorySettings;
